// simple-pdf.service.ts

import { Injectable } from '@angular/core';
import { SimplePdfModel } from '@app/shared/models/simple-pdf.model';
import {
  PdfMakeWrapper,
  Txt,
  Table,
  Cell,
  Img,
} from 'pdfmake-wrapper';
import * as pdfFonts from "pdfmake/build/vfs_fonts";

@Injectable({
  providedIn: 'root'
})
export class SimplePdfService {

  constructor() { }

  /**
   * @description Servicio para crear PDF simple
   * @param config Configuración del PDF
   */
  async simplePDF(config: SimplePdfModel) {
    const pdf = await this.buildPdf(config);
    pdf.create().download(`${config.fileName}.pdf`);
  }

  /**
   * Igual que `simplePDF` pero, en lugar de descargar, devuelve una URL de
   * objeto (blob) del PDF ya renderizado para poder MOSTRARLO en un iframe.
   *
   * Se agregó para poder previsualizar un documento antes de generarlo
   * (órdenes de servicio, CAF, etc.): antes el servicio sólo sabía descargar,
   * así que el usuario tenía que bajar el archivo para ver cómo quedaba.
   *
   * Recuerda liberar la URL con `URL.revokeObjectURL` al cerrar la vista.
   */
  async previewPDF(config: SimplePdfModel): Promise<string> {
    const pdf = await this.buildPdf(config);
    return new Promise<string>((resolve, reject) => {
      try {
        pdf.create().getBlob((blob: Blob) => resolve(URL.createObjectURL(blob)));
      } catch (err) {
        reject(err);
      }
    });
  }

  /** Construcción común del documento (compartida por descarga y preview). */
  private async buildPdf(config: SimplePdfModel) {
    const vfs = (pdfFonts as any).pdfMake?.vfs ?? (pdfFonts as any).vfs ?? pdfFonts;
    PdfMakeWrapper.setFonts({ pdfMake: { vfs } } as any);
    const pdf = new PdfMakeWrapper();

    const fontSize       = config.defaultStyle?.fontSize || 10;
    const pageSize       = config.pageSize               || 'A4';
    const pageOrientation= config.pageOrientation        || 'portrait';
    const pageMargins    = config.pageMargins            || [40, 40, 40, 40];

    pdf.pageSize(pageSize);
    pdf.pageOrientation(pageOrientation);

    if (pageMargins.length === 4) {
      pdf.pageMargins([pageMargins[0], pageMargins[1], pageMargins[2], pageMargins[3]]);
    } else {
      pdf.pageMargins([40, 40, 40, 40]);
    }

    pdf.defaultStyle({ fontSize });

    for (const section of config.sections) {
      await this.processSection(pdf, section);
    }

    if (config.footer) {
      pdf.footer(this.createFooter(config.footer));
    }

    return pdf;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // PROCESO DE SECCIONES
  // ─────────────────────────────────────────────────────────────────────────

  private async processSection(pdf: PdfMakeWrapper, section: any) {
    switch (section.type) {
      case 'header':
        await this.addHeader(pdf, section.header);
        break;
      case 'table':
        await this.addTable(pdf, section.table);
        break;
      case 'text':
        this.addText(pdf, section.text, section.textStyle);
        break;
      case 'image':
        await this.addImage(pdf, section.image);
        break;
      case 'lineBreak':
        pdf.add(pdf.ln(section.lineBreak || 1));
        break;
      case 'raw':
        // Contenido pdfmake tal cual (ver SectionModel.content).
        if (section.content !== undefined && section.content !== null) {
          pdf.add(section.content);
        }
        break;
      default:
        console.warn(`Tipo de sección desconocido: ${section.type}`);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // HEADER
  // ─────────────────────────────────────────────────────────────────────────

  private async addHeader(pdf: PdfMakeWrapper, header: any) {
    if (!header) return;

    const headerRows: any[] = [];

    // El número de columnas de la tabla del header debe ser CONSISTENTE en
    // todas las filas. Si hay campos personalizados (label|valor) o logo+título,
    // la tabla tiene 2 columnas; si no, 1. Mezclar filas de 1 y 2 celdas con un
    // único arreglo de `widths` provoca el crash de pdfmake
    // ("Cannot read properties of undefined (reading '_calcWidth')"), porque
    // pdfmake intenta resolver el ancho de una columna que no existe.
    const hasFields    = header.customFields?.length > 0;
    const hasLogoTitle = !!(header.logo && header.title);
    const numCols      = (hasFields || hasLogoTitle) ? 2 : 1;

    // Construye una fila que ocupa TODO el ancho. La celda principal hace
    // colSpan(numCols) y se rellenan las columnas restantes con celdas vacías
    // ({}) para que el número de celdas coincida con `widths`.
    const fullWidthRow = (node: any): any[] => {
      const row: any[] = [new Cell(node).colSpan(numCols).end];
      for (let i = 1; i < numCols; i++) row.push({});
      return row;
    };

    // Fila con logo y título
    if (header.logo || header.title) {
      if (header.logo && header.title) {
        headerRows.push([
          new Cell(await new Img(header.logo).width(100).alignment('center').build()).end,
          new Cell(new Txt(header.title).bold().fontSize(14).alignment('center').end).end,
        ]);
      } else if (header.logo) {
        headerRows.push(fullWidthRow(await new Img(header.logo).width(100).alignment('center').build()));
      } else {
        headerRows.push(fullWidthRow(new Txt(header.title).bold().fontSize(14).alignment('center').end));
      }
    }

    // Subtitle (ancho completo)
    if (header.subtitle) {
      headerRows.push(fullWidthRow(new Txt(header.subtitle).fontSize(12).alignment('center').end));
    }

    // Fecha actual (ancho completo)
    if (header.showDate) {
      const currentDate = new Date().toLocaleDateString('es-ES');
      headerRows.push(fullWidthRow(new Txt(`Fecha: ${currentDate}`).fontSize(10).alignment('right').end));
    }

    // Campos personalizados (2 columnas: label | valor)
    if (hasFields) {
      header.customFields.forEach((field: any) => {
        headerRows.push([
          new Cell(new Txt(`${field.label}:`).bold().end).end,
          new Cell(new Txt(field.value ?? '').end).end,
        ]);
      });
    }

    if (headerRows.length > 0) {
      const widths = numCols === 2 ? [200, '*'] : ['*'];
      pdf.add(
        new Table(headerRows)
          .widths(widths)
          .layout('noBorders')
          .margin([0, 0, 0, 20])
          .end
      );
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // TABLA
  // ─────────────────────────────────────────────────────────────────────────

  private async addTable(pdf: PdfMakeWrapper, table: any) {
    if (!table || !table.columns || !table.data) return;

    const rows       = [];
    const widths: any[] = [];
    const showHeader = table.showHeader !== false; // default true

    // Título de la tabla
    if (table.title) {
      pdf.add(new Txt(table.title).bold().fontSize(12).margin([0, 10, 0, 5]).end);
    }

    // ── Fila de encabezados ───────────────────────────────────────────────
    if (showHeader) {
      const headerRow: any[] = [];

      table.columns.forEach((col: any) => {
        const txt = new Txt(col.header)
          .bold()
          .alignment(col.alignment || 'center');

        if (table.headerStyle?.textColor) {
          txt.color(table.headerStyle.textColor);
        }

        const headerCell = new Cell(txt.end);

        if (table.headerStyle?.fillColor) {
          headerCell.fillColor(table.headerStyle.fillColor);
        }

        headerRow.push(headerCell.end);
      });

      rows.push(headerRow);
    }

    // ── Filas de datos ────────────────────────────────────────────────────
    for (const item of table.data) {
      const dataRow: any[] = [];

      for (const col of table.columns) {
        const key        = col.field ?? col.header;
        const cellValue  = this.getNestedProperty(item, key) ?? '';
        const isImage    = col.isImage === true;

        if (isImage && cellValue) {
          // Renderizar como imagen dentro de la celda
          const imgNode = await new Img(cellValue)
            .width(col.imageWidth || 80)
            .alignment(col.alignment || 'center')
            .build();
          dataRow.push(new Cell(imgNode).end);
        } else {
          let txt = new Txt(String(cellValue)).alignment(col.alignment || 'left');
          if (col.bold) txt = txt.bold();
          dataRow.push(new Cell(txt.end).end);
        }
      }

      rows.push(dataRow);
    }

    // ── Anchos ────────────────────────────────────────────────────────────
    table.columns.forEach((col: any) => widths.push(col.width || '*'));

    const tableComponent = new Table(rows)
      .widths(widths)
      .margin([0, 5, 0, 10]);

    if (table.showBorders !== false) {
      tableComponent.layout({
        hLineWidth: () => 1,
        vLineWidth: () => 1,
        hLineColor: () => '#cccccc',
        vLineColor: () => '#cccccc',
      });
    } else {
      tableComponent.layout('noBorders');
    }

    pdf.add(tableComponent.end);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // TEXTO
  // ─────────────────────────────────────────────────────────────────────────

  private addText(pdf: PdfMakeWrapper, text: string, style?: any) {
    if (!text) return;

    let txt = new Txt(text);

    if (style) {
      if (style.fontSize)  txt = txt.fontSize(style.fontSize);
      if (style.bold)      txt = txt.bold();
      if (style.alignment) txt = txt.alignment(style.alignment);
      if (style.margin)    txt = txt.margin(style.margin);
    }

    pdf.add(txt.end);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // IMAGEN INDEPENDIENTE
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Convierte una URL (o un data-uri) en el objeto imagen que pdfmake espera,
   * para poder incrustarla dentro de contenido CRUDO (`type: 'raw'`).
   *
   * `Img` de pdfmake-wrapper ya resuelve la descarga y el base64; aquí sólo se
   * expone públicamente porque los constructores de formatos (BIO-03, acuses)
   * arman su propio layout y necesitan el logotipo como una celda más.
   * Devuelve `null` si la imagen no se pudo cargar, para que el documento se
   * genere igual en vez de tronar.
   */
  async imageObject(src: string, width = 120): Promise<any | null> {
    try {
      return await new Img(src).width(width).build();
    } catch {
      return null;
    }
  }

  private async addImage(pdf: PdfMakeWrapper, image: any) {
    if (!image?.data) return;

    const imgBuilder = new Img(image.data).width(image.width || 200);

    if (image.alignment) imgBuilder.alignment(image.alignment);

    pdf.add(await imgBuilder.build());
  }

  // ─────────────────────────────────────────────────────────────────────────
  // FOOTER
  // ─────────────────────────────────────────────────────────────────────────

  private createFooter(footer: any) {
    return (currentPage: any, pageCount: any) => {
      const content: any[] = [];

      if (footer.showPageNumbers) {
        content.push(
          new Txt(`Página ${currentPage} de ${pageCount}`)
            .alignment(footer.alignment || 'center')
            .fontSize(10)
            .end
        );
      }

      if (footer.customText) {
        content.push(
          new Txt(footer.customText)
            .alignment(footer.alignment || 'center')
            .fontSize(10)
            .end
        );
      }

      return content;
    };
  }

  // ─────────────────────────────────────────────────────────────────────────
  // HELPERS
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * @description Obtiene propiedades de un objeto.
   * Primero intenta acceso directo (soporta keys con puntos como 'CANT.SOL'),
   * luego intenta acceso anidado separando por puntos.
   */
  private getNestedProperty(obj: any, path: string): any {
    if (!obj || !path) return null;

    // Acceso directo — soporta keys que contienen puntos (ej: 'CANT.SOL')
    if (Object.prototype.hasOwnProperty.call(obj, path)) {
      return obj[path];
    }

    // Acceso anidado (ej: 'cliente.nombre')
    return path.split('.').reduce((prev, curr) => (prev ? prev[curr] : null), obj);
  }

  /**
   * @description Formatea fecha
   */
  public formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day:   '2-digit',
      month: '2-digit',
      year:  'numeric',
    });
  }
}